import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { friendlyError } from "@/lib/errors";
import { AuthLegalLinks } from "@/components/layout/AuthLegalLinks";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setHasSession(!!data.session);
    });
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSaving(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (updateError) {
      setError(friendlyError(updateError.message));
      return;
    }
    setDone(true);
  };

  if (loading || hasSession === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-sm text-slate-400">Loading…</div>
      </div>
    );
  }

  // The recovery link carries a session via the URL hash. No session means the
  // link is invalid or has expired.
  if (!hasSession && !user && !done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-red-500 text-xl" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Link expired or invalid</h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            This password reset link is invalid or has expired. Request a new one to continue.
          </p>
          <Link
            to="/forgot-password"
            className="inline-block mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8">
        <div className="flex items-center justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center mr-3">
            <i className="ri-stack-line text-white" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">Tradivo</h1>
        </div>

        {done ? (
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-emerald-600 text-xl" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Password updated</h2>
            <p className="text-sm text-slate-500 mb-4">Your password has been reset successfully.</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Go to dashboard
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-slate-800 text-center mb-1">Choose a new password</h2>
            <p className="text-sm text-slate-500 text-center mb-6">Enter a new password for your account</p>

            {error && (
              <div role="alert" className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="reset-password" className="block text-sm font-medium text-slate-700 mb-1">New password</label>
                <input
                  id="reset-password"
                  type="password"
                  name="password"
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  placeholder="••••••••"
                />
                <p className="text-xs text-slate-400 mt-1">At least 6 characters.</p>
              </div>
              <div>
                <label htmlFor="reset-confirm" className="block text-sm font-medium text-slate-700 mb-1">Confirm new password</label>
                <input
                  id="reset-confirm"
                  type="password"
                  name="confirm"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-medium rounded-lg text-sm transition-colors whitespace-nowrap"
              >
                {saving ? "Updating..." : "Update password"}
              </button>
            </form>
          </>
        )}
        <AuthLegalLinks />
      </div>
    </div>
  );
}